import { useState, useEffect } from "react";
import { Check, Loader2, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCartInfo, savePreference } from "../../../store/thunk/cartThunk";
import { selectUser } from "../../../store/reducer/authSlice";
import toast from "react-hot-toast";
import { updateProductPreference } from "../../../store/reducer/cartSlice";

const PreferenceOption = ({ label, price, selected, onClick, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-between px-3 py-1 text-[12px] font-semibold rounded-md shadow-[0px_0px_3px_0px_rgba(0,_0,_0,_0.1)] transition-all
                ${
                  selected
                    ? "bg-primary-100 text-white border-red-600"
                    : "bg-white text-gray-800 border-gray-300"
                }
                ${
                  disabled
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer hover:shadow-md"
                }
            `}
    >
      <span>
        {label} | ${price.toFixed(2)}
      </span>
      <span className="ml-2">
        {selected ? (
          <Check className="bg-white text-primary-text rounded-full p-1 h-5 w-5 md:h-6 md:w-6" />
        ) : (
          <Plus className="p-1  bg-neutral-200 rounded-full stroke-1 h-5 w-5 md:h-6 md:w-6" />
        )}
      </span>
    </button>
  );
};

const Preferences = ({
  bottomSheet = false,
  page,
  customNotesView,
  productPreferences,
  cartPreference,
  productId,
  cartProductNotes,
  currentQty,
  onPreferenceChange,
  onPreferenceUpdate,
  disabled = false,
  currentSelections = {},
  isSubscriptionModal = false,
}) => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [availablePreferences, setAvailablePreferences] = useState([]);
  const [selectedPreferences, setSelectedPreferences] = useState({});
  const [cutOptions, setCutOptions] = useState([]);
  const [selectedCut, setSelectedCut] = useState(null);
  const [customNotes, setCustomNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  // Check if we should use parent callback instead of API
  const shouldUseParentCallback = bottomSheet && page === "home";
  useEffect(() => {
    if (shouldUseParentCallback && currentSelections) {
      // Set preferences from parent
      if (currentSelections.preferences && availablePreferences.length > 0) {
        const prefsObject = {};
        currentSelections.preferences.forEach((pref) => {
          const matchingPref = availablePreferences.find(
            (available) =>
              available.label === pref.name || available.id === pref.id
          );
          if (matchingPref) {
            prefsObject[matchingPref.id] = true;
          }
        });
        setSelectedPreferences(prefsObject);
      }

      // Set cut from parent
      if (currentSelections.cut && cutOptions.length > 0) {
        let cutId = null;
        if (typeof currentSelections.cut === "object") {
          const matchingCut = cutOptions.find(
            (cut) => cut.label === currentSelections.cut.name
          );
          cutId = matchingCut ? matchingCut.id : null;
        } else {
          cutId = currentSelections.cut;
        }
        if (cutId) {
          setSelectedCut(String(cutId));
        }
      }

      // Set notes from parent
      if (currentSelections.notes !== undefined) {
        setCustomNotes(currentSelections.notes);
      }
    }
  }, [
    shouldUseParentCallback,
    currentSelections,
    availablePreferences,
    cutOptions,
  ]);

  // Helper function to send data to parent
  const notifyParent = (updatedData = {}) => {
    if (shouldUseParentCallback && onPreferenceUpdate) {
      const currentSelectedPrefs =
        updatedData.selectedPreferences || selectedPreferences;
      const currentSelectedCut = updatedData.selectedCut || selectedCut;
      const currentCustomNotes = updatedData.custom_notes || customNotes;

      // Build selected preferences array
      const selectedPrefs = Object.keys(currentSelectedPrefs)
        .filter((key) => currentSelectedPrefs[key])
        .map((key) => {
          const pref = availablePreferences.find((p) => p.id === key);
          return pref
            ? {
                id: key,
                name: pref.label,
                price: pref.price,
              }
            : null;
        })
        .filter(Boolean);

      // Get selected cut option
      const selectedCutOption = cutOptions.find(
        (cut) => String(cut.id) === String(currentSelectedCut)
      );

      const dataToParent = {
        productId,
        preferences: selectedPrefs,
        cut: selectedCutOption
          ? {
              id: selectedCutOption.id,
              name: selectedCutOption.label,
              price: selectedCutOption.price,
            }
          : null,
        custom_notes: currentCustomNotes,
      };

      //console.log("Sending to parent:", dataToParent);
      onPreferenceUpdate(dataToParent);
    }
  };

  // Utility function to map preferences data from API response
  const mapPreferencesData = (preferencesData) => {
    if (!preferencesData) return { preferences: [], cuts: [] };

    let parsedData;
    try {
      parsedData =
        typeof preferencesData === "string"
          ? JSON.parse(preferencesData)
          : preferencesData;
    } catch (error) {
      console.error("Error parsing preferences data:", error);
      return { preferences: [], cuts: [] };
    }

    // Map preferences
    const preferences = [];
    if (parsedData.preference && Array.isArray(parsedData.preference)) {
      parsedData.preference.forEach((prefGroup) => {
        Object.entries(prefGroup).forEach(([key, value]) => {
          if (Array.isArray(value) && value[0]) {
            const item = value[0];
            preferences.push({
              id: key,
              label: item.label || "",
              price: parseFloat(item.price) || 0,
              isDefault: item.default === "1" || item.default === 1,
            });
          }
        });
      });
    }

    // Map cuts
    const cuts = [];
    if (parsedData.cut && Array.isArray(parsedData.cut)) {
      const cutsObj = parsedData.cut[0] || {};
      Object.entries(cutsObj).forEach(([key, value]) => {
        if (Array.isArray(value) && value[0]) {
          const item = value[0];
          cuts.push({
            id: key,
            label: item.label || "",
            price: parseFloat(item.price) || 0,
            isDefault: item.default === "1" || item.default === 1,
          });
        }
      });
    }

    return { preferences, cuts };
  };

  // Parse product preferences and set up options
  useEffect(() => {
    if (productPreferences) {
      const {
        preferences: formattedAvailablePreferences,
        cuts: formattedCutOptions,
      } = mapPreferencesData(productPreferences);

      setAvailablePreferences(formattedAvailablePreferences);
      setCutOptions(formattedCutOptions);

      // Initialize defaults only if NOT using parent callback or parent has no data
      if (
        !shouldUseParentCallback ||
        (!currentSelections.preferences && !currentSelections.cut)
      ) {
        //console.log("Initializing with defaults...");

        // Initialize default preferences
        const initialSelectedPrefs = {};
        formattedAvailablePreferences.forEach((pref) => {
          if (
            cartPreference?.preference?.some((p) => p.label === pref.label) ||
            pref.isDefault
          ) {
            initialSelectedPrefs[pref.id] = true;
          }
        });
        setSelectedPreferences(initialSelectedPrefs);

        // Initialize default cut
        const matchedCut = formattedCutOptions.find(
          (cut) => cut.label === cartPreference?.cut?.label
        );

        if (matchedCut) {
          setSelectedCut(matchedCut.id);
        } else {
          const defaultCut = formattedCutOptions.find((opt) => opt.isDefault);
          setSelectedCut(
            defaultCut ? defaultCut.id : formattedCutOptions[0]?.id
          );
        }

        // Notify parent with initial data if using parent callback
        // if (shouldUseParentCallback) {
        //   setTimeout(() => {
        //     notifyParent({
        //       selectedPreferences: initialSelectedPrefs,
        //       selectedCut: initialCutId,
        //       custom_notes: "",
        //     });
        //   }, 100);
        // }
      }
    }
  }, [productPreferences, cartPreference, shouldUseParentCallback]);

  // Toggle preference function
  const togglePreference = (item) => {
    //console.log("Toggling preference:", item.label);

    const newSelectedPreferences = {
      ...selectedPreferences,
      [item.id]: !selectedPreferences[item.id],
    };
    setSelectedPreferences(newSelectedPreferences);

    // If using parent callback, notify parent immediately
    if (shouldUseParentCallback) {
      setTimeout(() => {
        notifyParent({ selectedPreferences: newSelectedPreferences });
      }, 0);
      return;
    }

    // If not using parent callback, handle API calls (existing logic)
    if (currentQty === 0) {
      setTimeout(() => {
        notifyParentOfChange({ selectedPreferences: newSelectedPreferences });
      }, 0);
      return;
    }
    // Handle preference toggle for existing cart items
    if (currentQty !== 0) {
      const payload = {
        preference: item.label,
        preference_price: parseFloat(item.price),
        cuts: cartPreference?.cut?.label || "",
        cut_price: parseFloat(cartPreference?.cut?.price) || 0,
        custom_notes: customNotes,
        action: "save-preference",
        product_id: productId,
      };

      setIsUpdating(true);

      if (user) {
        dispatch(savePreference(payload))
          .unwrap()
          .then((res) => {
            // Check if API response indicates success
            if (res.status === 1) {
              dispatch(fetchCartInfo());
              setIsUpdating(false);
              //toast.success(res.message || "Preference updated successfully!");
            } else {
              // API returned status 0, handle as error
              setIsUpdating(false);
              toast.error(res.message || "Failed to update preference");
              // Revert the state change on error
              setSelectedPreferences(selectedPreferences);
            }
          })
          .catch((error) => {
            setIsUpdating(false);
            const errorMessage =
              error?.message || "Failed to update preference";
            toast.error(errorMessage);
            // Revert the state change on error
            setSelectedPreferences(selectedPreferences);
          });
      } else {
        const localPayload = {
          preference: item.label,
          preference_price: item.price,
          product_id: productId,
        };
        dispatch(updateProductPreference(localPayload));
        setIsUpdating(false);
        //toast.success("Preference updated successfully!");
      }
    }
  };

  // Handle cut change function
  const handleCutChange = (cut) => {
    //console.log("Changing cut to:", cut.label);

    const newSelectedCut = String(cut.id);
    setSelectedCut(newSelectedCut);

    // If using parent callback, notify parent immediately
    if (shouldUseParentCallback) {
      setTimeout(() => {
        notifyParent({ selectedCut: newSelectedCut });
      }, 0);
      return;
    }

    // If not using parent callback, handle API calls (existing logic)
    if (currentQty === 0) {
      setTimeout(() => {
        notifyParentOfChange({ selectedCut: newSelectedCut });
      }, 0);
      return;
    }

    // Handle API call for existing cart items
    if (currentQty !== 0) {
      const payload = {
        cuts: cut.label,
        cut_price: parseFloat(cut.price),
        custom_notes: customNotes,
        action: "save-preference",
        product_id: productId,
      };

      setIsUpdating(true);

      if (user) {
        dispatch(savePreference(payload))
          .unwrap()
          .then((res) => {
            // Check if API response indicates success
            if (res.status === 1) {
              dispatch(fetchCartInfo());
              setIsUpdating(false);
              //toast.success(res.message || "Cut option updated successfully!");
            } else {
              // API returned status 0, handle as error
              setIsUpdating(false);
              toast.error(res.message || "Failed to update cut option");
              // Revert the state change on error
              const matchedCut = cutOptions.find(
                (c) => c.label === cartPreference?.cut?.label
              );
              setSelectedCut(matchedCut ? matchedCut.id : cutOptions[0]?.id);
            }
          })
          .catch((error) => {
            setIsUpdating(false);
            const errorMessage =
              error?.message || "Failed to update cut option";
            toast.error(errorMessage);
            // Revert the state change on error
            const matchedCut = cutOptions.find(
              (c) => c.label === cartPreference?.cut?.label
            );
            setSelectedCut(matchedCut ? matchedCut.id : cutOptions[0]?.id);
          });
      } else {
        const localPayload = {
          cuts: cut.label,
          cut_price: cut.price,
          product_id: productId,
        };
        dispatch(updateProductPreference(localPayload));
        setIsUpdating(false);
        toast.success("Cut option updated successfully!");
      }
    }
  };

  // Save notes function
  const handleSaveNotes = () => {
    //console.log("Saving notes:", customNotes);

    // If using parent callback, notify parent immediately
    if (shouldUseParentCallback) {
      notifyParent({ custom_notes: customNotes });
      toast.success("Notes saved!");
      return;
    }

    // If not using parent callback, handle API calls (existing logic)
    if (currentQty !== 0) {
      const payload = {
        cuts: cartPreference?.cut?.label || "",
        cut_price: parseInt(cartPreference?.cut?.price) || 0,
        custom_notes: customNotes,
        action: "save-preference",
        product_id: productId,
      };

      setIsUpdating(true);

      if (user) {
        dispatch(savePreference(payload))
          .unwrap()
          .then((res) => {
            // Check if API response indicates success
            if (res.status === 1) {
              dispatch(fetchCartInfo());
              setIsUpdating(false);
              toast.success(res.message || "Notes saved successfully!");
            } else {
              // API returned status 0, handle as error
              setIsUpdating(false);
              toast.error(res.message || "Failed to save notes");
            }
          })
          .catch((error) => {
            setIsUpdating(false);
            const errorMessage = error?.message || "Failed to save notes";
            toast.error(errorMessage);
          });
      } else {
        const localPayload = {
          custom_notes: customNotes,
          product_id: productId,
        };
        dispatch(updateProductPreference(localPayload));
        setIsUpdating(false);
        toast.success("Notes saved successfully!");
      }
    }
  };

  const buildPreferencesPayload = (additionalData = {}) => {
    const currentSelectedPrefs =
      additionalData.selectedPreferences || selectedPreferences;
    const currentSelectedCut = additionalData.selectedCut || selectedCut;
    const currentCustomNotes = additionalData.custom_notes || customNotes;

    const selectedPrefs = Object.keys(currentSelectedPrefs)
      .filter((key) => currentSelectedPrefs[key])
      .map((key) => {
        const pref = availablePreferences.find((p) => p.id === key);
        return pref ? { label: pref.label, price: pref.price } : null;
      })
      .filter(Boolean);

    const selectedCutOption = cutOptions.find(
      (cut) => String(cut.id) === String(currentSelectedCut)
    );

    return {
      preference: selectedPrefs,
      cut: selectedCutOption
        ? { label: selectedCutOption.label, price: selectedCutOption.price }
        : null,
      custom_notes: currentCustomNotes,
      ...additionalData,
    };
  };

  const notifyParentOfChange = (updatedData = {}) => {
    if (onPreferenceChange) {
      const preferences = buildPreferencesPayload(updatedData);
      onPreferenceChange(preferences, updatedData.custom_notes || customNotes);
    }
  };

  useEffect(() => {
    setCustomNotes(cartProductNotes || "");
  }, [cartProductNotes]);

  const isDisabled = disabled || isUpdating;

  return (
    <div className="">
      <div className={` ${isDisabled ? "opacity-70" : ""}`}>
        <div className=" rounded-lg">
          {/* General Preferences Section */}
          {availablePreferences?.length > 0 && (
            <div className="col-span-12 md:col-span-8 mr-2">
              <p className="text-primary-text text-lg letter-spacing-1 font-semibold">
                Preference:
              </p>
              <div className="relative flex flex-wrap gap-2">
                {isUpdating && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                    <Loader2 className="animate-spin w-5 h-5 text-primary" />
                  </div>
                )}

                {availablePreferences.length > 0 ? (
                  availablePreferences.map((item) => (
                    <PreferenceOption
                      key={item.id}
                      label={item.label}
                      price={item.price}
                      selected={selectedPreferences[item.id]}
                      disabled={isDisabled || isUpdating}
                      onClick={() =>
                        !isDisabled && !isUpdating && togglePreference(item)
                      }
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-400 letter-spacing-1">
                    No specific preferences available.
                  </p>
                )}
              </div>
            </div>
          )}
          {/* Cut Options Section */}
          <div className="col-span-12 md:col-span-4 pt-3 md:pt-0 md:border-0 mt-3">
            <div className="block text-sm gap-1 relative">
              {isUpdating && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                  <Loader2 className="animate-spin w-5 h-5 text-primary ml-2" />
                </div>
              )}
              {cutOptions.length > 0 && (
                <div>
                  <p className="text-primary-text text-lg letter-spacing-1 font-semibold mb-2 bottom-sheet-cut-option-title">
                    Cut:
                  </p>

                  {cutOptions.map((cut) => (
                    <label
                      key={cut.id}
                      className={`inline-flex w-full md:w-fit letter-spacing-1 text-md items-center gap-x-2 pb-2 px-1 ${
                        isDisabled || isUpdating
                          ? "cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="radio"
                        name="cut"
                        value={cut.id}
                        checked={String(selectedCut) === String(cut.id)}
                        disabled={isDisabled || isUpdating}
                        onChange={() =>
                          !isDisabled && !isUpdating && handleCutChange(cut)
                        }
                        className="text-primary-100 h-4 w-4 pr-1"
                      />
                      {cut.label}{" "}
                      <span
                        className={`text-md font-semibold ${
                          isSubscriptionModal
                            ? "text-red-500"
                            : bottomSheet && page === "home"
                            ? "text-white"
                            : "text-red-500"
                        }`}
                      >
                        - $
                        {(typeof cut.price === "number"
                          ? cut.price
                          : 0
                        ).toFixed(2)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Custom Notes */}
        <div className={`md:border-0 md:mt-0 mt-0  ${customNotesView}`}>
          <p className="text-primary-text text-lg letter-spacing-1 font-semibold bottom-sheet-custom-notes-title">
            Custom Notes:
          </p>
          <textarea
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            disabled={isDisabled}
            rows={4}
            placeholder="Add Notes..."
            className={`mt-1 w-full border-b border-gray-300 p-2 text-sm resize-none focus:outline-none focus:border-gray-300 custome-notes-input ${
              isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
            } ${bottomSheet && page === "home" ? "placeholder-gray-600" : ""}`}
          />
          <button
            type="button"
            onClick={handleSaveNotes}
            disabled={isDisabled || !customNotes.trim()}
            className={`main-btn mt-2 px-7 py-2 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed text-md letter-spacing-1 font-medium`}
          >
            Save Notes
          </button>
        </div>
      </div>
    </div>
  );
};

export default Preferences;
